"use client";

import { useState, createRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useRouter } from "next/navigation";
import { cn, withUrl } from "@/lib/utils";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import PrimaryButton from "@/components/ui/primary-button";
import Link from "next/link";
import LINKS from "@/lib/links";
import { submitDemoRequest } from "@/components/forms/ci-app-demo-request-form-actions";
import Ringer from "@/components/ringer";
import ReCAPTCHA from "react-google-recaptcha";
import Checkbox from "@/components/ui/checkbox";
import { toast } from "sonner";
import styles from "../forms/ci-app-demo-request-form.module.css";

// Define the form schema using Zod
const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  facilityName: z.string().min(1, "Facility name is required"),
  message: z.string().optional(),
  receiveVideo: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type FormSubmissionData = FormValues & {
  recaptchaToken: string;
};

export interface CIAppDemoFormProps {
  className?: string;
  heading?: string;
  redirectUrl?: string;
}

const CIAppDemoForm = ({
  className,
  heading = "Transform Your Care Facility Today",
  redirectUrl = "/thank-you",
}: CIAppDemoFormProps) => {
  const router = useRouter();
  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptchaChange = setRecaptchaToken;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      facilityName: "",
      message: "",
      receiveVideo: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      if (!recaptchaToken) {
        toast.error("Please complete the CAPTCHA verification");
        setError("root.recaptcha", {
          type: "manual",
          message: "Please complete the CAPTCHA verification",
        });
        return;
      }

      const submissionData: FormSubmissionData = {
        ...data,
        recaptchaToken,
      };

      const result = await submitDemoRequest(submissionData);

      if (result.status === "success") {
        toast.success(result.message || "Demo request submitted successfully!");
        reset();
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);

        // Construct the redirect URL with search parameters
        const redirectUrlWithParams = new URL(
          redirectUrl,
          window.location.origin
        );
        redirectUrlWithParams.searchParams.set(
          "name",
          `${data.firstName} ${data.lastName}`
        );
        redirectUrlWithParams.searchParams.set("email", data.email);

        // Redirect to the configured URL with search parameters
        router.push(redirectUrlWithParams.toString());
      } else {
        toast.error(
          result.message || "There was an error submitting your request"
        );
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Form submission error:", error);
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-[384px] flex-col border border-[#FAF8F8] py-6 md:rounded-[24px] md:bg-white md:p-6 md:shadow-[4px_8px_24px_2px_rgba(82,77,75,0.12)] lg:min-h-[624px]",
        isSubmitting && "items-center justify-center",
        styles.formContainer,
        className
      )}
    >
      {isSubmitting ? (
        <div className="m-auto flex h-full min-h-[400px] items-center justify-center">
          <Ringer
            size={60}
            color="teal"
            colorShade="500"
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            <h3 className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3">
              {heading}
            </h3>
            <p className="font-roboto text-body-1-300 text-neutral-500">
              All required fields are marked with an asterisk.
            </p>
          </div>
          <form
            className="mt-4 flex flex-col"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Main form inputs with 24px gap */}
            <div className="flex flex-col gap-4 lg:gap-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  color="white"
                  id="first-name"
                  label="First Name"
                  autoComplete="given-name"
                  required
                  placeholder="First Name *"
                  error={!!errors.firstName}
                  errorMessage={errors.firstName?.message}
                  {...register("firstName")}
                />

                <Input
                  color="white"
                  id="last-name"
                  label="Last Name"
                  autoComplete="family-name"
                  required
                  placeholder="Last Name *"
                  error={!!errors.lastName}
                  errorMessage={errors.lastName?.message}
                  {...register("lastName")}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  color="white"
                  id="email"
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Email Address *"
                  error={!!errors.email}
                  errorMessage={errors.email?.message}
                  {...register("email")}
                />

                <Input
                  color="white"
                  id="phone"
                  label="Phone Number"
                  autoComplete="tel"
                  required
                  placeholder="Phone Number *"
                  error={!!errors.phoneNumber}
                  errorMessage={errors.phoneNumber?.message}
                  {...register("phoneNumber")}
                />
              </div>

              <div>
                <Input
                  color="white"
                  id="facility-name"
                  label="Facility Name"
                  required
                  placeholder="Facility Name *"
                  error={!!errors.facilityName}
                  errorMessage={errors.facilityName?.message}
                  {...register("facilityName")}
                />
              </div>

              <div>
                <Textarea
                  id="message"
                  label="Questions before your demo? (Optional)"
                  rows={3}
                  placeholder="Questions before your demo? (Optional)"
                  error={!!errors.message}
                  errorMessage={errors.message?.message}
                  {...register("message")}
                />
              </div>
            </div>

            {/* Bottom elements with 16px gap */}
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex items-start gap-2 md:items-center">
                <Checkbox
                  id="receive-video"
                  size="sm"
                  className="mt-1 md:mt-0"
                  {...register("receiveVideo")}
                />
                <label
                  htmlFor="receive-video"
                  className="font-roboto text-body-1-300 text-neutral-600"
                >
                  I would like to receive a video about the Care Indeed App
                </label>
              </div>

              <div>
                <p className="font-roboto text-body-1-300 text-neutral-400">
                  By submitting, you agree to Care Indeed&apos;s{" "}
                  <Link
                    href={withUrl(LINKS.termsOfUse.href)}
                    prefetch
                    className="underline"
                  >
                    Terms of Use
                  </Link>{" "}
                  and{" "}
                  <Link
                    href={withUrl(LINKS.privacyPolicy.href)}
                    prefetch
                    className="underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>

              {recaptchaKey && (
                <div>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaKey}
                    onChange={handleRecaptchaChange}
                  />
                </div>
              )}

              <div>
                <PrimaryButton
                  color="teal"
                  size="md"
                  disabled={isSubmitting}
                  type="submit"
                  className="flex w-fit"
                >
                  Request Demo
                </PrimaryButton>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default CIAppDemoForm;
